import { AiChatNetworkError, ChatMessageKind, } from '../model.js';
import { addMessageToChatSession, closeChatSession, createNewChatSession, findAiMessage, findDiscussion, getOpenChatSession, getOrCreateDiscussion, hasWelcomeMessage, toMarkdownMultiPartMessage, toTextMultiPartMessage, } from '../internal/discussion-utils.js';
import { QueryStatus, ClientResponseStatus, HitlStatus, } from '../client/model.js';
import { unreacheable } from '../internal/unreacheable.js';
import { debug } from '../internal/debug.js';
import * as ResponseUtils from '../internal/response-utils.js';
import { localize } from '../translations.js';
/**
 * Represents a controller used to discuss with the AI Chat server.
 */
export class PwcAiChatController {
    /**
     * Create a new instance of the chat controller.
     *
     * @param chatElement the chat element.
     * @param client the client.
     * @param pollingSettings the polling settings.
     * @param maxPollingRetries the max polling retries.
     */
    constructor(client, pollingSettings) {
        // the polling settings.
        this._pollingSettings = {
            interval: 3000,
            maxRetries: 40,
            rampUp: 5,
        };
        // the discussions saved in memory.
        this._discussions = [];
        // logger.
        this._log = debug('PwcAiChatController');
        this._pwcAiChatClientInstance = client;
        this._pollingSettings = pollingSettings;
    }
    /**
     * Handle a message posted by the user.
     */
    handlePostMessage(event, pwcAiChat) {
        // guard.
        if (!this._currentDiscussion) {
            throw new Error('Illegal state: no current discussion.');
        }
        // guard.
        if (!event.detail.prompt) {
            throw new Error('Illegal state: no prompt.');
        }
        // send the query.
        this._sendPromptAndWaitForResponse(this._currentDiscussion, event.detail.prompt, pwcAiChat);
    }
    /**
     * Handle a prompt posted programatically.
     */
    handleSendPrompt(prompt, pwcAiChat, contextOverride) {
        // guard.
        if (!this._currentDiscussion) {
            this._log('Unable to send prompt: no current discussion.');
            return;
        }
        // send the query.
        this._sendPromptAndWaitForResponse(this._currentDiscussion, prompt, pwcAiChat, contextOverride);
    }
    /**
     * Handle a retry post message action initiated by the user.
     */
    handleRetryPostMessage(pwcAiChat) {
        // guard.
        if (!this._currentDiscussion) {
            throw new Error('Illegal state: no current discussion.');
        }
        // get the latest prompt.
        const { latestPrompt } = this._currentDiscussion;
        // guard.
        if (!latestPrompt) {
            return;
        }
        // send the query.
        this._sendPromptAndWaitForResponse(this._currentDiscussion, latestPrompt, pwcAiChat);
    }
    /**
     * Handle a message posted by the user.
     */
    handleOpenChat(event, pwcAiChat) {
        // guard.
        if (!event.detail.flowName) {
            throw new Error('No flowname provided. Check your ai button parameters.');
        }
        // guard.
        if (!event.detail.context) {
            throw new Error('No context provided. Check your ai button parameters.');
        }
        // guard.
        if (event.detail.welcomeMessage.length === 0) {
            throw new Error('No welcome message provided. Check your ai button parameters.');
        }
        // guard.
        if (event.detail.suggestedPrompts === null) {
            throw new Error('Invalid suggested prompts value. Check your ai button parameters.');
        }
        // to create a new conversation when the context change,
        // then the context is part of the discussion id.
        const useContextAsId = event.detail.newConversationOnContextChange;
        // find the discussion.
        const discussion = getOrCreateDiscussion(this._discussions, event.detail.flowName, event.detail.context, useContextAsId, event.detail.enableHitl);
        // update the discussion welcome message (used by 'new chat' action later on).
        discussion.welcomeMessage = event.detail.welcomeMessage;
        // update the suggested prompts (used by 'new chat' action later on).
        discussion.suggestedPrompts = event.detail.suggestedPrompts;
        // update the discussion context.
        discussion.settings.context = event.detail.context;
        // get the open chat session.
        const openChatSession = getOpenChatSession(discussion);
        if (!hasWelcomeMessage(openChatSession)) {
            // check if the discussion contains a welcome message.
            addMessageToChatSession(ChatMessageKind.WELCOME_MESSAGE, openChatSession, toMarkdownMultiPartMessage(event.detail.welcomeMessage));
            // add suggested prompts.
            if (event.detail.suggestedPrompts.length > 0) {
                // check if the discussion contains suggested prompts.
                addMessageToChatSession(ChatMessageKind.SUGGESTED_PROMPTS, openChatSession, toTextMultiPartMessage(event.detail.suggestedPrompts));
            }
        }
        // update the current discussion.
        this._currentDiscussion = discussion;
        // cleanup the user input.
        pwcAiChat.clearUserInput();
        pwcAiChat.errorKind = AiChatNetworkError.NONE;
        pwcAiChat.errorId = undefined;
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Handle a context change.
     */
    handleContextChange(event) {
        // guard.
        if (!event.detail.flowName) {
            throw new Error('No flowname provided. Check your ai button parameters.');
        }
        // guard.
        if (!event.detail.context) {
            throw new Error('No context provided. Check your ai button parameters.');
        }
        // find the discussion.
        const discussion = findDiscussion(this._discussions, event.detail.flowName, event.detail.context, false);
        // here, the context can change: I update it.
        if (discussion) {
            discussion.settings.context = event.detail.context;
        }
    }
    /**
     * Handle the new chat session action.
     */
    handleNewChatSession(pwcAiChat) {
        // guard.
        if (!this._currentDiscussion) {
            throw new Error('Illegal state: no current discussion.');
        }
        // ge the open chat session.
        const openChatSession = getOpenChatSession(this._currentDiscussion);
        // this session has never been used, do not create a new one.
        if (!openChatSession.sessionId) {
            return;
        }
        // close the session.
        closeChatSession(this._currentDiscussion, openChatSession.sessionId);
        // create a new session.
        const newChatSession = createNewChatSession(this._currentDiscussion);
        // add the welcome message.
        addMessageToChatSession(ChatMessageKind.WELCOME_MESSAGE, newChatSession, toMarkdownMultiPartMessage(this._currentDiscussion.welcomeMessage));
        // add suggested prompts.
        if (this._currentDiscussion.suggestedPrompts.length > 0) {
            // check if the discussion contains suggested prompts.
            addMessageToChatSession(ChatMessageKind.SUGGESTED_PROMPTS, newChatSession, toTextMultiPartMessage(this._currentDiscussion.suggestedPrompts));
        }
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Handle the user reaction.
     */
    handleReaction(queryId, reaction, pwcAiChat) {
        // guard.
        if (!this._currentDiscussion) {
            throw new Error('Illegal state: no current discussion.');
        }
        // find the message.
        const message = findAiMessage(this._currentDiscussion, queryId);
        // guard.
        if (!message) {
            return;
        }
        // update the message.
        message.reaction = reaction;
        // send the reaction.
        this._pwcAiChatClientInstance.sendFeedbackReaction({
            flowName: this._currentDiscussion.settings.flowName,
            queryId,
            type: reaction,
        });
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Handle the user feedback comment.
     */
    handleFeedbackComment(queryId, comment) {
        // guard.
        if (!this._currentDiscussion) {
            throw new Error('Illegal state: no current discussion.');
        }
        // send the comment.
        this._pwcAiChatClientInstance.sendFeedbackComment({
            flowName: this._currentDiscussion.settings.flowName,
            queryId,
            comment,
        });
    }
    /**
     * Send the user prompt and poll at regular interval the server to get a response.
     */
    async _sendPromptAndWaitForResponse(currentDiscussion, prompt, pwcAiChat, contextOverride) {
        this._log('send prompt and wait for a response.');
        // trivial case: message already sent.
        if (currentDiscussion.isWaitingForResponse) {
            // block the UI, and display a warning.
            pwcAiChat.errorKind = AiChatNetworkError.GENERATING_RESPONSE;
            pwcAiChat.errorId = undefined;
            // update the chat element.
            this._touchChatElementDiscussion(pwcAiChat);
            return;
        }
        // message received, clear the input.
        pwcAiChat.clearUserInput();
        // get the open chat session.
        const currentChatSession = getOpenChatSession(currentDiscussion);
        // add the message to the discussion.
        addMessageToChatSession(ChatMessageKind.HUMAN, currentChatSession, toMarkdownMultiPartMessage(prompt));
        // send the query and wait for a response (Agent or HITL).
        currentDiscussion.isWaitingForResponse = true;
        // update the latest prompt entered by the user.
        currentDiscussion.latestPrompt = prompt;
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
        // if a session exits, use it.
        try {
            if (!currentDiscussion.isWaitingForHitlUserAnswer) {
                // send prompt to the Agent server.
                this._log('send prompt to Agent server.');
                // guard.
                if (currentChatSession.queryId) {
                    throw new Error('Illegal state: unexpected queryId, should be undefined.');
                }
                // variables.
                let queryResponse;
                // override the context if needed.
                let context;
                if (contextOverride) {
                    context = contextOverride;
                }
                else {
                    context = currentDiscussion.settings.context;
                }
                // send the query to Agent server.
                const clientResponse = await this._pwcAiChatClientInstance.sendQuery({
                    context,
                    flowName: currentDiscussion.settings.flowName,
                    prompt,
                    sessionId: currentChatSession.sessionId,
                });
                // get request status.
                const clientResponseStatus = clientResponse.status;
                // manage request status.
                switch (clientResponseStatus) {
                    case ClientResponseStatus.SUCCESS:
                        // get data and ...
                        queryResponse = clientResponse.data;
                        // ...update chat session.
                        currentChatSession.sessionId = queryResponse.sessionId;
                        currentChatSession.queryId = queryResponse.queryId;
                        break;
                    case ClientResponseStatus.BAD_REQUEST:
                    case ClientResponseStatus.INTERNAL_SERVER_ERROR:
                    case ClientResponseStatus.UNCATEGORIZED_ERROR:
                        this._handleClientResponseError(currentDiscussion, currentChatSession, clientResponse, pwcAiChat);
                        // if the query fails, no need to continue the process.
                        return;
                    default:
                        unreacheable(clientResponseStatus);
                }
            }
            else {
                // send prompt to the HITL server.
                this._log('send prompt to HITL server.');
                // guard.
                if (!currentChatSession.hitlKey) {
                    throw new Error('Illegal state: no HITL key available.');
                }
                // send the response to HITL server.
                const clientResponse = await this._pwcAiChatClientInstance.sendHitlAnswer(currentChatSession.hitlKey, prompt);
                // get request status.
                const clientResponseStatus = clientResponse.status;
                // manage request status.
                switch (clientResponseStatus) {
                    case ClientResponseStatus.SUCCESS:
                        // update discussion.
                        currentChatSession.hitlKey = undefined;
                        currentDiscussion.isWaitingForHitlUserAnswer = false;
                        break;
                    case ClientResponseStatus.BAD_REQUEST:
                    case ClientResponseStatus.INTERNAL_SERVER_ERROR:
                    case ClientResponseStatus.UNCATEGORIZED_ERROR:
                        this._handleClientResponseError(currentDiscussion, currentChatSession, clientResponse, pwcAiChat);
                        // if the query fails, no need to continue the process.
                        return;
                    default:
                        unreacheable(clientResponseStatus);
                }
            }
            // update error flag.
            pwcAiChat.errorKind = AiChatNetworkError.NONE;
            pwcAiChat.errorId = undefined;
            if (currentDiscussion.settings.enableHitl) {
                // HITL enabled: wait for a response from the HITL server OR from the Agent server.
                await this._waitForAgentOrHitlResponse(currentDiscussion, currentChatSession, pwcAiChat);
            }
            else {
                // HITL disabled: wait for a response from the Agent server.
                await this._waitForAgentResponse(currentDiscussion, currentChatSession, pwcAiChat);
            }
        }
        catch (error) {
            // log error.
            this._log(error);
            // manage connection errors.
            this._handleConnectionError(currentDiscussion, currentChatSession, pwcAiChat);
        }
    }
    /**
     * Wait for a response of the agent.
     */
    async _waitForAgentResponse(currentDiscussion, currentChatSession, pwcAiChat) {
        let retryCounter = 0;
        let continuePolling = true;
        /* eslint-disable no-await-in-loop */
        do {
            this._log(`wait for Agent server response (${retryCounter}/${this._pollingSettings.maxRetries})`);
            // variables.
            let queryStatusResponse;
            // call agent server.
            const clientResponse = await this._delayedGetQueryStatusClientResponse(currentChatSession, retryCounter);
            // get request status.
            const clientResponseStatus = clientResponse.status;
            // manage request status.
            switch (clientResponseStatus) {
                case ClientResponseStatus.SUCCESS:
                    queryStatusResponse = clientResponse.data;
                    break;
                case ClientResponseStatus.BAD_REQUEST:
                case ClientResponseStatus.INTERNAL_SERVER_ERROR:
                case ClientResponseStatus.UNCATEGORIZED_ERROR:
                    this._handleClientResponseError(currentDiscussion, currentChatSession, clientResponse, pwcAiChat);
                    continuePolling = false;
                    break;
                default:
                    unreacheable(clientResponseStatus);
            }
            if (queryStatusResponse) {
                // get the status of the response.
                const { status } = queryStatusResponse;
                switch (status) {
                    case QueryStatus.COMPLETED:
                        this._handleQueryCompleted(currentDiscussion, currentChatSession, queryStatusResponse, pwcAiChat);
                        continuePolling = false;
                        break;
                    case QueryStatus.FAILED:
                        this._handleQueryFailure(currentDiscussion, currentChatSession, pwcAiChat);
                        continuePolling = false;
                        break;
                    case QueryStatus.PROCESSING:
                    case QueryStatus.QUEUED:
                        // bump the counter.
                        retryCounter += 1;
                        // trivial case : max retries reach.
                        if (retryCounter > this._pollingSettings.maxRetries) {
                            this._handleQueryFailure(currentDiscussion, currentChatSession, pwcAiChat);
                            continuePolling = false;
                        }
                        break;
                    default:
                        unreacheable(status);
                }
            }
            // if the chat session is closed, stop the process.
            if (!currentChatSession.isOpen) {
                continuePolling = false;
            }
        } while (continuePolling);
        /* eslint-enable no-await-in-loop */
    }
    /**
     * Poll Agent server.
     */
    async _delayedGetQueryStatusClientResponse(chatSession, retryCounter) {
        return new Promise((resolve, reject) => {
            window.setTimeout(() => {
                if (!chatSession.queryId) {
                    throw new Error('Illegal state: undefined queryId.');
                }
                // execute query.
                this._pwcAiChatClientInstance
                    .getQueryStatus(chatSession.queryId)
                    .then(response => {
                    resolve(response);
                })
                    .catch(e => {
                    reject(e);
                });
            }, this._rampUpDelay(retryCounter));
        });
    }
    /**
     * Wait for a response of the HITL server.
     *
     * @return true if the user should answer the question.
     */
    async _waitForAgentOrHitlResponse(currentDiscussion, currentChatSession, pwcAiChat) {
        let retryCounter = 0;
        let continuePolling = true;
        /* eslint-disable no-await-in-loop */
        do {
            this._log(`wait for Agent or HITL server response (${retryCounter}/${this._pollingSettings.maxRetries})`);
            // variables.
            let hitlResponse;
            let agentResponse;
            // poll for the response.
            const clientResponses = await Promise.all([
                this._delayedGetHitlQuestionResponse(currentChatSession, retryCounter),
                this._delayedGetQueryStatusClientResponse(currentChatSession, retryCounter),
            ]);
            // get client responses.
            const hitlClientResponse = clientResponses[0];
            const agentClientResponse = clientResponses[1];
            // get client response status.
            const hitlClientResponseStatus = hitlClientResponse.status;
            // manage client response status.
            switch (hitlClientResponseStatus) {
                case ClientResponseStatus.SUCCESS:
                    hitlResponse = hitlClientResponse.data;
                    break;
                case ClientResponseStatus.BAD_REQUEST:
                case ClientResponseStatus.INTERNAL_SERVER_ERROR:
                case ClientResponseStatus.UNCATEGORIZED_ERROR:
                    this._handleClientResponseError(currentDiscussion, currentChatSession, hitlClientResponse, pwcAiChat);
                    continuePolling = false;
                    break;
                default:
                    unreacheable(hitlClientResponseStatus);
            }
            // get request status.
            const agentClientResponseStatus = agentClientResponse.status;
            // manage request status.
            switch (agentClientResponseStatus) {
                case ClientResponseStatus.SUCCESS:
                    agentResponse = agentClientResponse.data;
                    break;
                case ClientResponseStatus.BAD_REQUEST:
                case ClientResponseStatus.INTERNAL_SERVER_ERROR:
                case ClientResponseStatus.UNCATEGORIZED_ERROR:
                    this._handleClientResponseError(currentDiscussion, currentChatSession, agentClientResponse, pwcAiChat);
                    continuePolling = false;
                    break;
                default:
                    unreacheable(agentClientResponseStatus);
            }
            if (hitlResponse && agentResponse) {
                // process the responses.
                // get the status of the responses.
                const hitlResponseStatus = hitlResponse.status;
                const agentResponseStatus = agentResponse.status;
                // validate couple of statuses.
                if (!PwcAiChatController._isValidCoupleOfHitlAndAgentStatuses(hitlResponseStatus, agentResponseStatus)) {
                    // case: state is not valid.
                    this._log(`invalid couple of statuses Agent[${agentResponseStatus}] HITL[${hitlResponseStatus}]`);
                    // error.
                    this._handleQueryFailure(currentDiscussion, currentChatSession, pwcAiChat);
                    continuePolling = false;
                }
                else {
                    // case: state is valid.
                    // check HITL status.
                    switch (hitlResponseStatus) {
                        case HitlStatus.QUESTION:
                            this._handleHitlQuestion(currentDiscussion, currentChatSession, hitlResponse, pwcAiChat);
                            continuePolling = false;
                            break;
                        case HitlStatus.NO_QUESTION:
                            // continue polling.
                            break;
                        default:
                            unreacheable(hitlResponseStatus);
                    }
                    // check Agent status.
                    switch (agentResponseStatus) {
                        case QueryStatus.COMPLETED:
                            this._handleQueryCompleted(currentDiscussion, currentChatSession, agentResponse, pwcAiChat);
                            continuePolling = false;
                            break;
                        case QueryStatus.FAILED:
                            this._handleQueryFailure(currentDiscussion, currentChatSession, pwcAiChat);
                            continuePolling = false;
                            break;
                        case QueryStatus.PROCESSING:
                        case QueryStatus.QUEUED:
                            // continue polling.
                            break;
                        default:
                            unreacheable(agentResponseStatus);
                    }
                }
                if (continuePolling) {
                    // bump the counter.
                    retryCounter += 1;
                    // trivial case : max retries reach.
                    if (retryCounter > this._pollingSettings.maxRetries) {
                        this._handleQueryFailure(currentDiscussion, currentChatSession, pwcAiChat);
                        continuePolling = false;
                    }
                }
            }
            // if the chat session is closed, stop the process.
            if (!currentChatSession.isOpen) {
                continuePolling = false;
            }
        } while (continuePolling);
        /* eslint-enable no-await-in-loop */
    }
    /**
     * Validate the couple of statuses.
     */
    static _isValidCoupleOfHitlAndAgentStatuses(hitlStatus, queryStatus) {
        const VALID_USE_CASES = [
            // use case: HITL question.
            [QueryStatus.PROCESSING, HitlStatus.QUESTION],
            // use case: continue polling.
            [QueryStatus.QUEUED, HitlStatus.NO_QUESTION],
            [QueryStatus.PROCESSING, HitlStatus.NO_QUESTION],
            // use case: Agent response.
            [QueryStatus.COMPLETED, HitlStatus.NO_QUESTION],
            [QueryStatus.FAILED, HitlStatus.NO_QUESTION],
        ];
        return VALID_USE_CASES.some(it => it[0] === queryStatus && it[1] === hitlStatus);
    }
    /**
     * Poll HITL server.
     */
    async _delayedGetHitlQuestionResponse(chatSession, retryCounter) {
        return new Promise((resolve, reject) => {
            window.setTimeout(() => {
                if (!chatSession.sessionId) {
                    throw new Error('Illegal state: undefined sessionId.');
                }
                // execute query.
                this._pwcAiChatClientInstance
                    .getHitlQuestion(chatSession.sessionId)
                    .then(response => {
                    resolve(response);
                })
                    .catch(e => {
                    reject(e);
                });
            }, this._rampUpDelay(retryCounter));
        });
    }
    /**
     * Handle a completed query.
     */
    _handleQueryCompleted(currentDiscussion, currentChatSession, response, pwcAiChat) {
        this._log(`handle query completed.`);
        // convert the response into ui data.
        const messageParts = ResponseUtils.parseMessageParts(response);
        // get the events.
        if (messageParts) {
            const eventMessageParts = ResponseUtils.parseEventMessageParts(response);
            // send the events if any.
            if (eventMessageParts) {
                PwcAiChatController._handleSendEventMessageParts(eventMessageParts);
            }
        }
        // update the discussion.
        addMessageToChatSession(ChatMessageKind.AI, currentChatSession, messageParts);
        // update values.
        currentChatSession.queryId = undefined;
        currentDiscussion.isWaitingForResponse = false;
        currentDiscussion.latestPrompt = undefined;
        pwcAiChat.errorKind = AiChatNetworkError.NONE;
        pwcAiChat.errorId = undefined;
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Handle a query failure.
     */
    _handleQueryFailure(currentDiscussion, currentChatSession, pwcAiChat) {
        this._log(`handle query failure.`);
        // update the discussion.
        addMessageToChatSession(ChatMessageKind.AI, currentChatSession, toMarkdownMultiPartMessage(localize(pwcAiChat.locale, 'ai.chat.error.query-failure')));
        // update values.
        currentChatSession.queryId = undefined;
        currentDiscussion.isWaitingForResponse = false;
        pwcAiChat.errorKind = AiChatNetworkError.NONE;
        pwcAiChat.errorId = undefined;
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Handle a client response error.
     */
    _handleClientResponseError(currentDiscussion, currentChatSession, clientResponseError, pwcAiChat) {
        this._log(`handle client response error.`);
        // udpate values.
        currentChatSession.queryId = undefined;
        currentDiscussion.isWaitingForResponse = false;
        // get the status.
        const { status } = clientResponseError;
        // manage client response status.
        switch (status) {
            case ClientResponseStatus.BAD_REQUEST:
                pwcAiChat.errorKind = AiChatNetworkError.INVALID_REQUEST;
                pwcAiChat.errorId = clientResponseError.errorID;
                break;
            case ClientResponseStatus.INTERNAL_SERVER_ERROR:
                pwcAiChat.errorKind = AiChatNetworkError.INTERNAL_SERVER_ERROR;
                pwcAiChat.errorId = clientResponseError.requestID;
                break;
            case ClientResponseStatus.UNCATEGORIZED_ERROR:
                pwcAiChat.errorKind = AiChatNetworkError.INTERNAL_SERVER_ERROR;
                pwcAiChat.errorId = undefined;
                break;
            default:
                unreacheable(status);
        }
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Handle any connection error.
     */
    _handleConnectionError(currentDiscussion, currentChatSession, pwcAiChat) {
        this._log(`handle connection error.`);
        // update values.
        currentChatSession.queryId = undefined;
        currentDiscussion.isWaitingForResponse = false;
        pwcAiChat.errorKind = AiChatNetworkError.CONNECTIVITY_ISSUES;
        pwcAiChat.errorId = undefined;
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Handle HITL question.
     */
    _handleHitlQuestion(currentDiscussion, currentChatSession, response, pwcAiChat) {
        this._log(`handle HITL question.`);
        // update the discussion.
        addMessageToChatSession(ChatMessageKind.AI, currentChatSession, toMarkdownMultiPartMessage(response.question));
        // update values.
        currentDiscussion.isWaitingForResponse = false;
        currentDiscussion.latestPrompt = undefined;
        currentDiscussion.isWaitingForHitlUserAnswer = true;
        currentChatSession.hitlKey = response.key;
        pwcAiChat.errorKind = AiChatNetworkError.NONE;
        pwcAiChat.errorId = undefined;
        // update the chat element.
        this._touchChatElementDiscussion(pwcAiChat);
    }
    /**
     * Touching the chat discussion trigger the refresh.
     */
    _touchChatElementDiscussion(pwcAiChat) {
        // guard.
        if (!this._currentDiscussion) {
            throw new Error('Illegal state: no current discussion.');
        }
        // update the discussion.
        pwcAiChat.discussion = { ...this._currentDiscussion };
    }
    /**
     * Get ramp-up delay.
     */
    _rampUpDelay(currentIteration) {
        // trivial case: no ramp up set.
        if (this._pollingSettings.rampUp <= 0) {
            return this._pollingSettings.interval;
        }
        // interpolate interval according the following rule:
        // if currentIteration = 0
        // -> then delay = 0s
        // if currentIteration = rampUp
        // -> then delay = interval
        return Math.min((this._pollingSettings.interval / this._pollingSettings.rampUp) *
            currentIteration, this._pollingSettings.interval);
    }
    /**
     * Send events.
     */
    static _handleSendEventMessageParts(eventMessageParts) {
        for (const eventMessagePart of eventMessageParts) {
            // eslint-disable-next-line no-undef
            prosAiChatUi.notifyEventListener(eventMessagePart.kind, eventMessagePart.data);
        }
    }
}
//# sourceMappingURL=pwc-ai-chat-controller.js.map